import { Box } from 'braid-design-system';
import { useEffect, useMemo, useState } from 'react';

import Error from 'src/components/Error/Error';
import InitialView from 'src/components/JobDetail/InitialView';
import JobDetail from 'src/components/JobDetail/JobDetail';
import Loading from 'src/components/Loading/Loading';
import JobList from 'src/components/Recommendations/JobList';
import { ScreenReaderSkipTarget } from 'src/components/ScreenReaderSkipTarget/ScreenReaderSkipTarget';
import SplitViewLayout from 'src/components/SplitViewLayout/SplitViewLayout';
import { useSetSolHashesToLocalStorage } from 'src/context/solContext/solContext';
import type { RecommendedGlobalJob } from 'src/queries/types';

import JobCount from '../JobCount';
import ZeroRecommendations from '../ZeroRecommendations';

interface RecommendationsContentProps {
  hasError: boolean;
  hasAuthError: boolean;
  jobCount: number;
  globalJobs: RecommendedGlobalJob[];
  loading: boolean;
}

const useSelectedRecommendation = (jobs: RecommendedGlobalJob[]) => {
  const [selectedJob, setSelectedJob] = useState<RecommendedGlobalJob>();
  const selectedJobListingPosition = useMemo(
    () =>
      (jobs.findIndex((job) => job.job.id === selectedJob?.job.id) || 0) + 1,
    [jobs, selectedJob],
  );
  const [clickedJobCount, setClickedJobCount] = useState(0);

  useEffect(() => {
    if (selectedJob) {
      const matchingJob = jobs.find((job) => job.job.id === selectedJob.job.id);
      if (matchingJob && matchingJob !== selectedJob) {
        setSelectedJob(matchingJob);
      }
    }
  }, [jobs, selectedJob]);

  useEffect(() => {
    if (selectedJob) {
      setClickedJobCount(clickedJobCount + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob]);

  return {
    selectedJob,
    setSelectedJob,
    selectedJobListingPosition,
    clickedJobCount,
  };
};

export const JOB_COUNT_PADDING: {
  paddingTop: Parameters<typeof Box>[0]['paddingTop'];
  paddingBottom: Parameters<typeof Box>[0]['paddingBottom'];
} = {
  paddingTop: 'none',
  paddingBottom: 'small',
};

const RecommendationsContent = ({
  hasError,
  hasAuthError,
  jobCount,
  globalJobs,
  loading,
}: RecommendationsContentProps) => {
  const {
    selectedJob,
    setSelectedJob,
    selectedJobListingPosition,
    clickedJobCount,
  } = useSelectedRecommendation(globalJobs);

  const setUpLocalStorage = useSetSolHashesToLocalStorage();
  useEffect(() => {
    if (globalJobs.length) {
      setUpLocalStorage(globalJobs);
    }
  }, [globalJobs, setUpLocalStorage]);

  if (hasAuthError) {
    return null;
  }

  if (loading) {
    return <Loading />;
  }

  if (!loading && jobCount === 0 && !hasError) {
    return <ZeroRecommendations />;
  }

  if (hasError) {
    return <Error data-automation="loading-recs-error-alert" />;
  }

  const hasSelectedJob = Boolean(selectedJob);

  return (
    <SplitViewLayout
      ListView={
        loading ? (
          <Loading />
        ) : (
          <>
            <ScreenReaderSkipTarget name="start-of-content" />
            <Box {...JOB_COUNT_PADDING}>
              <JobCount count={jobCount} />
            </Box>
            <JobList
              globalJobs={globalJobs}
              jobCount={jobCount}
              hasError={hasError}
              hasAuthError={hasAuthError}
              setCurrentJob={setSelectedJob}
              selectedJobId={selectedJob?.job.id}
            />
          </>
        )
      }
      DetailView={
        selectedJob ? (
          <JobDetail
            selectedJob={selectedJob}
            selectedJobListingPosition={selectedJobListingPosition}
          />
        ) : (
          <InitialView />
        )
      }
      clickedJobCount={clickedJobCount}
      totalJobCount={jobCount}
      hasSelectedJob={hasSelectedJob}
    />
  );
};

export default RecommendationsContent;
